import React, { useEffect, useState } from "react";
import { Skeleton } from "antd";
import SpecialOffersApi from "../../../../api-services/special-offers-api.service";
import type { SpecialOffer } from "../../../../api-services/special-offers-api.service";
import type { TravelMode } from "../Homepage";
import PromoTourCard from "./PromoTourCard";
import GroundTourCard from "./GroundTourCard";
import SectionHeader from "./SectionHeader";
import ScrollCarousel from "./ScrollCarousel";
import { brandTooltipText, useGdBrands } from "../brand-logos";
import useBreakpoint from "../../../../utils/useBreakpoint";

// "Special Offer" - API-backed tagged-tour cards filtered by Tour/Cruise mode.
export default function SpecialOffers({ mode }: { mode: TravelMode }) {
  const [offers, setOffers] = useState<SpecialOffer[]>([]);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const categoryType = mode === "cruise" ? "cruise" : mode === "ground" ? "ground" : "series";
  const { brands: gdBrands } = useGdBrands();
  const isMobile = useBreakpoint();

  useEffect(() => {
    let active = true;
    setLoading(true);

    SpecialOffersApi.getOffers(categoryType)
      .then(({ offers: nextOffers, tagIds: nextTagIds }) => {
        if (!active) return;
        setOffers(nextOffers);
        setTagIds(nextTagIds);
      })
      .catch(() => {
        if (!active) return;
        setOffers([]);
        setTagIds([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [categoryType]);

  if (!loading && offers.length === 0) return null;

  // Ground Tour's listing page (frontend#628) has its own route and query
  // contract (see utils/groundListingParams) rather than /web/listing's
  // type=ground, and has no tags filter - only promotions.
  const viewAllHref = mode === "ground"
    ? (tagIds.length ? `/web/ground-tour?promotions=${tagIds.join(",")}` : "/web/ground-tour")
    : tagIds.length
      ? `/web/listing?type=${categoryType}&tags=${tagIds.join(",")}`
      : `/web/listing?type=${categoryType}`;

  return (
    <section className="v2-section" id="special-offers-v2">
      <SectionHeader title="Special Offer" subtitle="Don't miss out on this limited-time deal!" viewAllHref={viewAllHref} />
      <ScrollCarousel trackClassName="v2-offers__track" step={isMobile ? 356 : 290}>
        {loading && [1, 2, 3, 4].map((placeholder) => (
          <div className="v2-offer-card v2-offer-card--compact v2-offer-card--loading" key={placeholder}>
            <div className="v2-offer-card__media">
              <Skeleton active />
            </div>
            <div className="v2-offer-card__body">
              <div className="v2-offer-card__content">
                <span className="v2-offer-card__skeleton-line v2-offer-card__skeleton-line--loc" />
                <span className="v2-offer-card__skeleton-line v2-offer-card__skeleton-line--title" />
                <span className="v2-offer-card__skeleton-line v2-offer-card__skeleton-line--title-short" />
                <div className="v2-offer-card__tag-row">
                  <span className="v2-offer-card__skeleton-line v2-offer-card__skeleton-line--tag" />
                </div>
              </div>
              <div className="v2-offer-card__pricing">
                <span className="v2-offer-card__skeleton-line v2-offer-card__skeleton-line--from" />
                <span className="v2-offer-card__skeleton-line v2-offer-card__skeleton-line--save" />
              </div>
              <span className="v2-offer-card__skeleton-line v2-offer-card__skeleton-line--price" />
            </div>
          </div>
        ))}

        {!loading && offers.map((offer) => (
          mode === "ground" ? (
            <GroundTourCard
              key={offer.id}
              variant="compact"
              image={offer.image}
              promotion={offer.badge}
              tourType={offer.tourType || "private"}
              tourStart={offer.tourStart}
              title={offer.title}
              brand={offer.tier}
              flightsIncluded={offer.flightsIncluded}
              originalPrice={offer.originalPrice}
              save={offer.save}
              price={offer.price}
              minPax={offer.minPax || 1}
              href={offer.href}
            />
          ) : (
            <PromoTourCard
              key={offer.id}
              variant="compact"
              image={offer.image}
              ribbon={offer.badge}
              tags={offer.tags}
              brandLogo={offer.brandLogo}
              brandName={offer.brandName}
              brandDescription={brandTooltipText(gdBrands, offer.category)}
              destination={offer.location}
              departFrom={offer.from}
              title={offer.title}
              guaranteed={offer.guaranteed}
              originalPrice={offer.originalPrice}
              save={offer.save}
              price={offer.price}
              footnote={offer.footnote}
              href={offer.href}
            />
          )
        ))}
      </ScrollCarousel>
    </section>
  );
}
